import { ExternalLink, Layers, MoreHorizontal, Undo2 } from "lucide-react";
import { stripeCustomerUrl, type PlansT, type SubscriptionRow } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { Pop, useClosePops } from "../../shell/Pop";

/**
 * The 44px column at the end of a subscription row.
 *
 * Only entries that can do something for THIS row. The old table drew all three
 * controls inline and simply left the gaps empty, so a list of thirty sites was
 * a ragged field of icons that said more about which rows were on Stripe than
 * about what you could do to them. Here the customer link appears only when
 * there is a customer to open, and the refund only when there is a live
 * subscription on an account to refund it to.
 */
export function SubsRowMenu({
  row,
  t,
  onAssign,
  onRefund,
}: {
  row: SubscriptionRow;
  t: PlansT;
  onAssign: (row: SubscriptionRow) => void;
  onRefund: (row: SubscriptionRow) => void;
}) {
  const tc = useT();
  const close = useClosePops();

  return (
    // Above the row-covering button overlay, or the menu would never get the click.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={`crm-cb${open ? " is-on" : ""}`}
            title={tc("common.more")}
            aria-label={tc("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            onAssign(row);
          }}
        >
          <Layers className="size-4" />
          {t("subs.assign")}
        </button>

        {row.providerCustomerId ? (
          <a
            className="crm-mi"
            href={stripeCustomerUrl(row.providerCustomerId)}
            target="_blank"
            rel="noreferrer"
            onClick={close}
          >
            <ExternalLink className="size-4" />
            {t("subs.stripe.openCustomer")}
          </a>
        ) : null}

        {/* The refund targets the ACCOUNT, not the site, so a site whose account
            is missing (a host site) has nothing to charge back. */}
        {row.providerSubscriptionId && row.accountId ? (
          <>
            <div className="crm-menu-sep" />
            <button
              type="button"
              className="crm-mi crm-mi-danger"
              onClick={() => {
                close();
                onRefund(row);
              }}
            >
              <Undo2 className="size-4" />
              {t("subs.refund")}
            </button>
          </>
        ) : null}
      </Pop>
    </span>
  );
}
